import React, { useState } from 'react';
import { IconButton, Popover, Box, Typography, Tooltip, Paper } from '@mui/material';
import { HelpOutline } from '@mui/icons-material';
import { keyframes } from '@mui/system';

// Define pulse animation
const pulse = keyframes`
  0% {
    transform: scale(1);
    opacity: 0.7;
  }
  50% {
    transform: scale(1.1);
    opacity: 1;
  }
  100% {
    transform: scale(1);
    opacity: 0.7;
  }
`;

interface HelpComponentProps {
  children: React.ReactNode;
  title?: string;
  size?: 'small' | 'medium' | 'large';
  maxWidth?: number;
  placement?: 'top' | 'bottom' | 'left' | 'right';
}

const HelpComponent: React.FC<HelpComponentProps> = ({
  children,
  title = 'Help',
  size = 'medium',
  maxWidth = 300,
  placement = 'bottom',
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'help-popover' : undefined;

  return (
    <>
      <Tooltip title={title} placement={placement}>
        <IconButton
          aria-describedby={id}
          onClick={handleClick}
          size={size}
          sx={{
            mr: -1,
            color: 'primary.main',
            '&:hover': {
              backgroundColor: 'primary.main',
              color: 'white',
              animation: `${pulse} 1s ease-in-out infinite`,
            },
            transition: 'all 0.2s ease-in-out',
          }}
        >
          <HelpOutline />
        </IconButton>
      </Tooltip>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: placement === 'top' ? 'top' : 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: placement === 'top' ? 'bottom' : 'top',
          horizontal: 'left',
        }}
        sx={{
          '& .MuiPopover-paper': {
            maxWidth: maxWidth,
            minWidth: 200,
          },
        }}
      >
        <Paper elevation={3} sx={{ p: 2 }}>
          <Box>
            {title && (
              <Typography
                variant="subtitle2"
                component="div"
                sx={{
                  fontWeight: 600,
                  mb: 1,
                  color: 'primary.main',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  pb: 0.5,
                }}
              >
                {title}
              </Typography>
            )}
            <Box sx={{ color: 'text.secondary' }}>{children}</Box>
          </Box>
        </Paper>
      </Popover>
    </>
  );
};

export default HelpComponent;
